import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Switch, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";
import ReactHotToaster from "../../components/common/ReactHotToaster";

const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const textMain = "#111827";
const textMuted = "#6b7280";
const accentGreen = "#0f9d6b";
const accentGreenBg = "#e6f6ef";
const negRed = "#c0392b";
const negRedBg = "#fdecea";

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const PaymentGatewayConfiguration = () => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadGateways = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/payment/payment-gateway/",
      });
      setGateways(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      console.log("payment-gateway list error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load payment gateways");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGateways();
  }, []);

  const handleOpen = () => {
    setName("");
    setIsActive(true);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter a gateway name");
      return;
    }
    setSaving(true);
    try {
      const res = await axiosConfig({
        method: "post",
        url: "/payment/payment-gateway/",
        data: {
          name: name.trim().toLowerCase(),
          is_active: isActive,
        },
      });
      const successMsg =
        typeof res?.data?.message === "string"
          ? res.data.message
          : typeof res?.data?.response === "string"
            ? res.data.response
            : "Payment gateway saved successfully";
      toast.success(successMsg);
      handleClose();
      loadGateways();
    } catch (error) {
      const errMsg = error?.response?.data?.message ?? error?.response?.data?.response;
      toast.error(typeof errMsg === "string" ? errMsg : "Failed to save payment gateway");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (gateway) => {
    const nextActive = !gateway.is_active;
    setTogglingId(gateway.id);
    try {
      await axiosConfig({
        method: "put",
        url: "/payment/payment-gateway/",
        data: { id: gateway.id, is_active: nextActive },
      });
      setGateways((prev) =>
        prev.map((g) => (g.id === gateway.id ? { ...g, is_active: nextActive } : g))
      );
      toast.success(`${gateway.name} ${nextActive ? "activated" : "deactivated"}`);
    } catch (error) {
      const errMsg = error?.response?.data?.message ?? error?.response?.data?.response;
      toast.error(typeof errMsg === "string" ? errMsg : "Failed to update payment gateway");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 4px 20px rgba(26,32,53,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SettingsSuggestIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              Payment Gateway Configuration
            </Typography>
            <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>
              Enable or disable payment gateways used at checkout
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          sx={{
            background: "#016551", borderRadius: "8px", textTransform: "none",
            fontWeight: 600, fontSize: 13, px: 2.5,
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
          }}
        >
          Add Gateway
        </Button>
      </Box>

      {/* Table */}
      <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "16px 20px", borderBottom: `1px solid ${cardBorder}` }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: textMain }}>Payment Gateways</Typography>
          <Button
            variant="outlined"
            onClick={loadGateways}
            sx={{
              borderColor: cardBorder, color: textMain, borderRadius: "8px", textTransform: "none",
              fontWeight: 600, fontSize: 13, px: 2,
              "&:hover": { background: "#f3f4f6", borderColor: cardBorder },
            }}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr">
                {["ID", "Name", "Status", "Created At", "Updated At", "Action"].map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: "left", fontSize: 11.5, color: textMuted, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: "0.3px",
                      p: "10px 20px", whiteSpace: "nowrap", borderBottom: `1px solid ${cardBorder}`,
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {loading ? (
                <Box component="tr">
                  <Box component="td" colSpan={6} sx={{ p: 5, textAlign: "center" }}>
                    <CircularProgress size={26} sx={{ color: accentGreen }} />
                  </Box>
                </Box>
              ) : gateways.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={6} sx={{ p: 5, textAlign: "center", fontSize: 13, color: textMuted }}>
                    No payment gateways found
                  </Box>
                </Box>
              ) : (
                gateways.map((g) => (
                  <Box component="tr" key={g.id}>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{g.id}</Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 13, color: textMain, fontWeight: 600, textTransform: "capitalize", borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{g.name}</Box>
                    <Box component="td" sx={{ p: "12px 20px", borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11.5, fontWeight: 700,
                          color: g.is_active ? accentGreen : negRed,
                          background: g.is_active ? accentGreenBg : negRedBg,
                          borderRadius: "999px", padding: "3px 10px",
                        }}
                      >
                        {g.is_active ? "Active" : "Inactive"}
                      </Box>
                    </Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 12, color: textMuted, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{fmtDate(g.created_at)}</Box>
                    <Box component="td" sx={{ p: "12px 20px", fontSize: 12, color: textMuted, borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>{fmtDate(g.updated_at)}</Box>
                    <Box component="td" sx={{ p: "12px 20px", borderBottom: `1px solid ${cardBorder}`, whiteSpace: "nowrap" }}>
                      {togglingId === g.id ? (
                        <CircularProgress size={18} sx={{ color: accentGreen }} />
                      ) : (
                        <Switch
                          checked={!!g.is_active}
                          onChange={() => handleToggleActive(g)}
                          color="success"
                        />
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add Gateway Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: textMain }}>Add Payment Gateway</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Gateway Name"
            fullWidth
            margin="dense"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. phonepe"
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="success" />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: textMain }}>
              {isActive ? "Active" : "Inactive"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ color: textMuted, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !name.trim()}
            sx={{
              background: "#016551", borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              "&:disabled": { background: "#9ca3af" },
            }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ReactHotToaster />
    </Box>
  );
};

export default PaymentGatewayConfiguration;
